import { createBrowserRouter, Navigate, type RouteObject } from 'react-router'

import { AppLayout } from './layout/app-layout'
import { FeedbackManagementRoute } from './feedback/feedback-management-route'
import { MyFeedbackPage } from './feedback/my-feedback-page'
import { ModuleRoute } from './layout/module-route'
import { moduleReferences, modules } from './modules'
import { ForbiddenPage } from './pages/forbidden'
import { HomePage } from './pages/home'
import { NotFoundPage } from './pages/not-found'
import { ModuleNavigationPage } from './navigation/module-navigation-page'
import { NotificationsPage } from './notifications/notifications-page'
import { VerwaltungPage } from './verwaltung/verwaltung-page'
import { UsagePage } from './usage/usage-page'
import { SettingsLayout } from './settings/settings-layout'
import { FeedbackSettingsPage } from './settings/feedback-settings-page'

/**
 * Der Routenbaum entsteht aus den gefundenen Manifesten.
 *
 * Jedes Modul bekommt `/<id>/*`; was darunter liegt, bestimmt das Modul selbst
 * über die Routen aus seiner `module.config.ts`. Die werden erst beim Öffnen
 * geladen (siehe {@link ModuleRoute}).
 *
 * Hinweis für Modulentwickler: Datenladen läuft über TanStack Query, nicht über
 * React-Router-Loader. Die Modulrouten werden als verschachtelte Routen
 * gerendert, deshalb greifen `loader` und `action` dort nicht.
 */
const moduleRoutes: RouteObject[] = modules.map((module) => ({
  path: `${module.id}/*`,
  element: <ModuleRoute module={module} availableModules={moduleReferences} />,
}))

/**
 * Erzeugt den Router. Bewusst eine Funktion und keine Konstante: so liest jede
 * Instanz die aktuelle Adresse, und Tests können die Anwendung mehrfach mit
 * unterschiedlichen Startadressen rendern.
 */
export function createAppRouter() {
  return createBrowserRouter([
    {
      path: '/',
      Component: AppLayout,
      children: [
        { index: true, Component: HomePage },
        { path: 'forbidden', Component: ForbiddenPage },
        {
          path: 'verwaltung',
          element: <Navigate to="/einstellungen/module-rechte" replace />,
        },
        {
          path: 'module-navigation',
          element: <Navigate to="/einstellungen/module" replace />,
        },
        { path: 'nutzungsanalyse', Component: UsagePage },
        {
          path: 'benachrichtigungen',
          element: <Navigate to="/einstellungen/benachrichtigungen" replace />,
        },
        {
          path: 'einstellungen',
          Component: SettingsLayout,
          children: [
            { path: 'feedback', Component: FeedbackSettingsPage },
            { path: 'module', element: <ModuleNavigationPage section="modules" /> },
            { path: 'module-rechte', Component: VerwaltungPage },
            {
              path: 'navigation',
              element: <ModuleNavigationPage section="navigation" />,
            },
            { path: 'benachrichtigungen', Component: NotificationsPage },
          ],
        },
        { path: 'feedback/:id?', Component: FeedbackManagementRoute },
        { path: 'mein-feedback/:id?', Component: MyFeedbackPage },
        ...moduleRoutes,
        { path: '*', Component: NotFoundPage },
      ],
    },
  ])
}
